import type { Facet } from "@otto-ec/heureka-components/facet";
import type { FacetValue } from "@otto-ec/heureka-components/facet-value";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { element } from "../util/Utils";
import { isNotSelected, isSelected, SvelteFacetValue } from "./SvelteFacetValue";
import { eventQBus } from "../types/EventQBus";
import { hasStatus, updateStatus } from "../tracking/FeatureTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import { SveltePopularFacetValuesWrapper } from "./SveltePopularFacetValues";
import { Filter } from "./Filter";
import { selectedCategoryPathValues } from "../categorypath/categoryPathStore";
import { get } from "svelte/store";
import { isActive } from "../toggle/Toggle";

const CATEGORY_PATH_FACET_ID = "categorypath";
const CATEGORY_PATH_FACET_ELEMENT_SELECTOR = `.ts_heureka_svelteFacet[data-facet-id='${CATEGORY_PATH_FACET_ID}']`;
const FACET_CLASS = "hc_facet";
const SELECTED_FACET_VALUES_CLASS = `hc_facetValueGroup--selected`;
const SELECTED_FACET_VALUES_SELECTOR = `.${SELECTED_FACET_VALUES_CLASS}`;
const FACET_RANGES_CLASS = `${FACET_CLASS}--range`;

export class SvelteFacet {
  static readonly factory = HeurekaElementFactory.byElement("fieldset", FACET_CLASS, SvelteFacet);

  constructor(readonly facet: HTMLFieldSetElement) {}

  /*                  */

  static facetId(id: string, root?: NonElementParentNode): SvelteFacet | undefined {
    return SvelteFacet.factory.byId(`facet_${id}`, root);
  }

  static facetName(facetName: string, root?: ParentNode): SvelteFacet | undefined {
    return SvelteFacet.factory.pick(`fieldset.hc_facet[name="${facetName}"]`, root);
  }

  /*                       */

  get filter(): Filter | undefined {
    return Filter.factory.declare(this.facet.form);
  }

  get name(): string | undefined {
    return this.facet.dataset?.facetName;
  }

  get selectedContent(): HTMLElement | null {
    return this.facet.querySelector(SELECTED_FACET_VALUES_SELECTOR);
  }

  get selected(): boolean {
    return !!this.selectedContent;
  }

  get values(): SvelteFacetValue[] {
    return SvelteFacetValue.factory.all(this.facet);
  }

  get selectedValues(): SvelteFacetValue[] {
    return this.values.filter(isSelected);
  }

  get unselectedValues(): SvelteFacetValue[] {
    return this.values.filter(isNotSelected);
  }

  get isForcedRelevant(): boolean {
    return this.facet?.dataset.facetVisibility === "forcedRelevant";
  }

  static ranges(root?: ParentNode): Facet[] {
    return SvelteFacet.factory.pickAll(`${SvelteFacet.factory.selector}.${FACET_RANGES_CLASS}`, root);
  }

  get sveltePopularFacetValuesWrapper(): SveltePopularFacetValuesWrapper | undefined {
    return SveltePopularFacetValuesWrapper.factory.pick(undefined, this.facet);
  }

  get featureStatus(): FeatureStatus | null {
    return (this.facet.dataset.tsFeatureStatus as FeatureStatus) || null;
  }

  updateFeatureStatus(statusToUpdate: FeatureStatus, updatedStatus: FeatureStatus) {
    if (this.featureStatus === statusToUpdate) {
      updateStatus(this.facet, updatedStatus);
    }
    this.values.forEach((value) => {
      if (hasStatus(value.facetValue)) {
        value.updateStatus(statusToUpdate, updatedStatus);
      }
    });
  }
}

/*               */
export function prepareCategoryPathFacet(fragment: DocumentFragment) {
  const categoryPathElement = element(CATEGORY_PATH_FACET_ELEMENT_SELECTOR, fragment);
  const oldFacet = SvelteFacet.facetId(CATEGORY_PATH_FACET_ID);
  /*                                                                 */
  if (oldFacet && categoryPathElement && categoryPathElement.dataset.serialized) {
    const newFacet: Facet = JSON.parse(categoryPathElement.dataset.serialized);
    const oldSelectedValuesIds = isActive("HEUREKA_2117_MOUNT_NBF_DIRECTLY_IN_REFINEMENT_BAR")
      ? Array.from(get(selectedCategoryPathValues)).map((value) => value.id)
      : oldFacet.selectedValues.map((value) => value.id);
    const isNewFacetSelected = !!newFacet.values.find((value: FacetValue) => value.checked === true);

    if (isNewFacetSelected) {
      /*                                                   */
      oldSelectedValuesIds.forEach((id) => {
        const value = newFacet.values.find((value: FacetValue) => value.id === id);
        if (value) {
          value.checked = true;
        }
      });
    }
    categoryPathElement.dataset.serialized = JSON.stringify(newFacet);
  }
}

function categoryPathFacetInitialization() {
  SvelteFacet.facetName("kategorien")?.selectedValues.forEach((facetValue) => {
    /*                                                    */
    facetValue.toggleChecked(facetValue.checked);
  });
}

export function registerCategoryPathFacet() {
  eventQBus.on("ftfind.dresonRule.resolved", () => {
    categoryPathFacetInitialization();
  });
}
